// see https://github.com/Arnaud73/ngx-matomo

import { inject, Injectable, InjectionToken } from '@angular/core';

declare const window: {
  [key: string]: any;
  prototype: Window;
  new (): Window;
};

export const FEATURE_TRACKER_INIT_MATOMO = new InjectionToken<boolean>('app-feature-tracker-matomo-initialize');
export const FEATURE_TRACKER_MATOMO_APPLICATION_ID = new InjectionToken<number>('app-feature-tracker-matomo-application-id');
export const FEATURE_TRACKER_MATOMO_ENDPOINT = new InjectionToken<string>('app-feature-tracker-matomo-endpoint-url');
export const FEATURE_TRACKER_MATOMO_IS_IFRAME = new InjectionToken<boolean>('app-feature-tracker-is-iframe');

@Injectable({
  providedIn: 'root',
})
export class MatomoService {
  private initMatomo = inject(FEATURE_TRACKER_INIT_MATOMO);
  private applicationId = inject(FEATURE_TRACKER_MATOMO_APPLICATION_ID);
  private matomoEndpoint = inject(FEATURE_TRACKER_MATOMO_ENDPOINT);
  private isIframe = inject(FEATURE_TRACKER_MATOMO_IS_IFRAME);

  constructor() {
    window._paq = window._paq || [];
    if (!this.initMatomo) {
      return;
    }

    if (!this.matomoEndpoint && !this.matomoEndpoint.length) {
      throw new Error('Please provide your Matomo endpoint by using the FEATURE_TRACKER_MATOMO_ENDPOINT variable.');
    }

    if (this.applicationId === -1) {
      throw new Error('Please provide your Matomo application Id by using the FEATURE_TRACKER_MATOMO_APPLICATION_ID variable.');
    }

    this.injectMatomo(this.matomoEndpoint, this.applicationId, this.isIframe);
  }

  /**
   * Injects Matomo to the document
   * @param url URL to Matomo
   * @param id ID of your Application in Matomo
   */
  injectMatomo(url: string, id: number, isInInframe = false) {
    if (isInInframe) {
      window._paq.push(['setCookieSameSite', 'None']);
    }

    window._paq.push(['trackPageView']);
    window._paq.push(['enableLinkTracking']);
    (() => {
      const u = url;
      window._paq.push(['setTrackerUrl', u + 'matomo.php']);
      window._paq.push(['setSiteId', id.toString()]);
      const d = document,
        g = d.createElement('script'),
        s = d.getElementsByTagName('script')[0];
      g.type = 'text/javascript';
      g.async = true;
      g.defer = true;
      g.src = u + 'matomo.js';
      s.parentNode!.insertBefore(g, s);
    })();
  }

  /**
   * Logs an event with an event category (Videos, Music, Games...), an event action (Play, Pause, Duration,
   * Add Playlist, Downloaded, Clicked...), and an optional event name and optional numeric value.
   * @param category
   * @param action
   * @param name
   * @param value
   */
  trackEvent(category: string, action: string, name?: string, value?: number): void {
    try {
      const args: any[] = [category, action];
      if (name) {
        args.push(name);
      }
      if (value) {
        args.push(value);
      }
      window._paq.push(['trackEvent', ...args]);
    } catch (e) {
      if (!(e instanceof ReferenceError)) {
        throw e;
      }
    }
  }
}
